import React, { useState, useRef, useEffect } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from './Button';
import { postTotpChallenge } from '../endpoints/auth/totp-challenge_POST.schema';
import { useAuth } from '../helpers/useAuth';
import styles from './TwoFactorChallenge.module.css';

interface TwoFactorChallengeProps {
  tempToken: string;
  onSuccess: () => void;
  onCancel: () => void;
}

export const TwoFactorChallenge: React.FC<TwoFactorChallengeProps> = ({
  tempToken,
  onSuccess,
  onCancel,
}) => {
  const [digits, setDigits] = useState<string[]>(['', '', '', '', '', '']);
  const [isVerifying, setIsVerifying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [useBackup, setUseBackup] = useState(false);
  const [backupCode, setBackupCode] = useState('');
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const { onLogin } = useAuth();

  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  const handleDigitChange = (index: number, value: string) => {
    const sanitized = value.replace(/\D/g, '');
    if (!sanitized && !value) {
      const next = [...digits];
      next[index] = '';
      setDigits(next);
      if (index > 0) inputRefs.current[index - 1]?.focus();
      return;
    }
    const next = [...digits];
    next[index] = sanitized.slice(-1);
    setDigits(next);
    if (sanitized && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
    if (index === 5 && sanitized) {
      const code = [...next.slice(0, 5), sanitized.slice(-1)].join('');
      if (code.length === 6) submitCode(code);
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length === 6) {
      const next = pasted.split('');
      setDigits(next);
      inputRefs.current[5]?.focus();
      submitCode(pasted);
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
    if (e.key === 'Enter') {
      const code = digits.join('');
      if (code.length === 6) submitCode(code);
    }
  };

  const submitCode = async (code: string) => {
    setError(null);
    setIsVerifying(true);
    try {
      const result = await postTotpChallenge({ tempToken, token: code });
      onLogin(result.user);
      onSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Verification failed');
      setDigits(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    } finally {
      setIsVerifying(false);
    }
  };

  const submitBackup = async () => {
    const clean = backupCode.trim();
    if (clean.length < 8) {
      setError('Enter a valid backup code (XXXX-XXXX)');
      return;
    }
    setError(null);
    setIsVerifying(true);
    try {
      const result = await postTotpChallenge({ tempToken, token: clean });
      onLogin(result.user);
      onSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Invalid backup code');
    } finally {
      setIsVerifying(false);
    }
  };

  return (
    <div className={styles.container}>
      {/* Brand logo header */}
      <div className={styles.logoSection}>
        <img src="/logo.png" alt="Tasking.tech" className={styles.logoIcon} />
        <span className={styles.logoText}>
          tasking<span className={styles.logoTextAccent}>.tech</span>
        </span>
      </div>

      <div className={styles.card}>
        <h2 className={styles.title}>Two-Factor Authentication</h2>
        <p className={styles.subtitle}>
          {useBackup
            ? 'Enter one of your backup recovery codes.'
            : 'Enter the 6-digit code sent to your device.'}
        </p>

        {error && <div className={styles.error}>{error}</div>}

        {!useBackup ? (
          <>
            <div className={styles.digitRow} onPaste={handlePaste}>
              {digits.map((d, i) => (
                <input
                  key={i}
                  id={`totp-digit-${i}`}
                  name={`totp-digit-${i}`}
                  ref={(el) => {
                    inputRefs.current[i] = el;
                  }}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  className={styles.digitInput}
                  value={d}
                  onChange={(e) => handleDigitChange(i, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(i, e)}
                  disabled={isVerifying}
                  autoComplete="one-time-code"
                />
              ))}
            </div>
            <Button
              onClick={() => submitCode(digits.join(''))}
              disabled={isVerifying || digits.join('').length !== 6}
              className={styles.verifyBtn}
            >
              {isVerifying ? (
                <>
                  <Loader2 size={16} className={styles.spinner} /> Verifying...
                </>
              ) : (
                'Verify'
              )}
            </Button>
          </>
        ) : (
          <>
            <input
              id="totp-backup-code"
              name="totp-backup-code"
              type="text"
              className={styles.backupInput}
              placeholder="XXXX-XXXX"
              value={backupCode}
              onChange={(e) => setBackupCode(e.target.value.toUpperCase())}
              onKeyDown={(e) => e.key === 'Enter' && submitBackup()}
              disabled={isVerifying}
              autoFocus
            />
            <Button
              onClick={submitBackup}
              disabled={isVerifying || backupCode.trim().length < 8}
              className={styles.verifyBtn}
            >
              {isVerifying ? (
                <>
                  <Loader2 size={16} className={styles.spinner} /> Verifying...
                </>
              ) : (
                'Use Backup Code'
              )}
            </Button>
          </>
        )}

        <button
          className={styles.switchLink}
          onClick={() => {
            setUseBackup(!useBackup);
            setError(null);
          }}
        >
          {useBackup ? 'Use authenticator code instead' : 'Resend Code'}
        </button>
      </div>

      <button className={styles.cancelLink} onClick={onCancel}>
        Cancel and return to login
      </button>
    </div>
  );
};
